// import { useMemo } from 'react';
import { ScatterplotLayer, TextLayer } from '@deck.gl/layers';
import {DataFilterExtension} from '@deck.gl/extensions';
import CollisionFilterExtensionFix from '../utils/collision-filter-fix';
import { depthToColor } from '../utils/layer_style';
import { useOGCCollection, type GeoJsonFeature } from '../utils/ogc';
import type { PickingInfo } from '@deck.gl/core';
import { use, useMemo } from 'react';

type PastFloodEventFeatureProperties = {
  event_id: string;
  start_time: Date;
  end_time: Date;
  max_depth_inches: number;
};

export type PastFloodEventFeature = GeoJsonFeature & {
  properties: PastFloodEventFeatureProperties;
};


// export const transformEventWithDepthData = (d: GeoJsonFeature) => {
//     d.id = d.properties.deployment_id;
//     d.properties.time = new Date(d.properties.time);
//     return d as EventDepthDataFeature;
// }

// export const useEventDepthData = (timeRange: [Date | undefined, Date | undefined]) => {
//   const data = useOGCFunction('postgisftw.event_depth_data', { from_time: timeRange[0]?.toISOString(), to_time: timeRange[1]?.toISOString(), limit: 100000 });
//   data.features = useMemo(() => data.features?.map(transformEventWithDepthData), [data.features]);
//   return data;
// };

const transformPastFloodEvent = (d: GeoJsonFeature) => {
    d.id = d.properties.event_id;
    d.properties.start_time = new Date(d.properties.start_time);
    d.properties.end_time = new Date(d.properties.end_time);
    return d as PastFloodEventFeature;
}
export const usePastFloodEvents = (timeRange?: [Date, Date, boolean]) => {
  timeRange = useMemo(() => {
    return timeRange || [new Date('2020-10-01T00:00:00Z'), new Date(Date.now() + 86400000), false];
  }, [timeRange]);
  const start_time = timeRange[0].toISOString();
  const end_time = timeRange[1].toISOString();
  const data = useOGCCollection('ogc.past_flood_events', {
    limit: 10000,
    filter: start_time && end_time ? `end_time >= '${start_time}' AND start_time <= '${end_time}'` : undefined,
  });
  data.features = useMemo(() => data.features?.map(transformPastFloodEvent), [data.features]);
  return data as { features: PastFloodEventFeature[] | undefined; loading: boolean; error: any };
}


export default function usePastFloodEventsLayer(
  features: PastFloodEventFeature[] | undefined,
  { onClick, visible = true, currentTime, timeRange }: { onClick?: (info: PickingInfo<GeoJsonFeature>) => void; visible?: boolean; currentTime?: Date, timeRange?: [Date, Date] }
) {
  // console.log('Past flood events features:', features);

  const t0 = currentTime?.getTime();
  const inside = (f: PastFloodEventFeature) => {
    if (!t0) return 1;
    const s = f.properties.start_time?.getTime?.() ?? +new Date(f.properties.start_time);
    const e = f.properties.end_time?.getTime?.() ?? +new Date(f.properties.end_time);
    return (s <= t0 && t0 <= (e || t0)) ? 1 : -1;
  };

  return (!features ? [] : [
    new ScatterplotLayer({
      id: 'past-flood-events-scatter',
      data: features,
      visible,
      
      pickable: true,
      radiusMinPixels: 4,
      radiusUnits: 'pixels',
      stroked: true,
      filled: true,
      // getLineColor: [255, 255, 255, 200],
      getLineColor: [200, 200, 240, 200],
      getLineWidth: 0,
      lineWidthMinPixels: 0,
      minZoom: 8,
      maxZoom: 14,

      onClick,
      getPosition: (f: GeoJsonFeature): [number, number, number] => [f.geometry.coordinates[0], f.geometry.coordinates[1], f.properties.max_depth_inches * 25.4 / 1000],
      getFillColor: (f: GeoJsonFeature) => depthToColor(f.properties.max_depth_inches),
      getRadius: (f: GeoJsonFeature) => {
        const depth = f.properties.max_depth_inches;
        return Math.min(Math.round(depth ? 8 + 8 * depth / 24 : 4), 20);
      },
      // onClick,
      collisionTestProps: {radiusScale: 0.05},
      getFilterValue: (f: PastFloodEventFeature) => inside(f),
      filterRange: [1, 1],
      extensions: [
        // new CollisionFilterExtension(),
        new DataFilterExtension({ filterSize: 1 })
      ],

      updateTriggers: {
        // getFillColor: [currentTime],
        // getRadius: [currentTime],
        // getText: [currentTime],
        // getPosition: [currentTime],
        // getCollisionPriority: [t0],
        getFilterValue: [t0],
      },
      parameters: { depthTest: false },
    }),
    new TextLayer({
      id: 'past_flood_events-labels',
      data: features,
      visible,
      getPosition: (f: GeoJsonFeature) => [...f.geometry.coordinates, f.properties.max_depth_inches * 25.4 / 100 + 1],
      getText: (f: GeoJsonFeature) => {
        const depth = f.properties.max_depth_inches;
        // return depth ? `${(Math.round(depth*10)/10).toFixed(1)}` : '';
        return depth ? `${(depth >= 6 ? depth.toFixed(0) : depth.toFixed(1))}` : '';
      },
      getSize: (f: GeoJsonFeature) => {
        const depth = f.properties.max_depth_inches;
        return depth ? (depth > 6 ? 14 : 10) : 0;
      },

      getAlignmentBaseline: 'center',
      billboard: true,
      getAnchor: 'middle',
      getColor: [255, 255, 255, 255],
      // getSize: 12,
      fontWeight: 900,
      fontFamily: "'Verdana, Roboto, Helvetica Neue', Arial, Helvetica, sans-serif",
      parameters: { depthTest: false },
      collisionGroup: 'event-labels',

      getCollisionPriority: function(f: GeoJsonFeature) {
        return f.properties.max_depth_inches * 25.4 - 1000;
      },
      // getFilterValue: function(f) {
      //   const t1 = f.properties.time.getTime();
      //   // return t0 >= t1 && Math.abs(t0 - t1 - closestTimeDelta[f.id!.toString()]) < 1 ? 1 : -1;
      //   const depth = f.properties.max_depth_inches;
      //   return t0 >= t1 && t1 > t0 - 30 * 60 * 1000 && Math.abs(t0 - t1 - closestTimeDelta[f.id!.toString()]) < 1 ? 1 : -1;
      // },
      extensions: [
        new CollisionFilterExtensionFix(),
        new DataFilterExtension({ filterSize: 1 })
      ],
      getFilterValue: (f: PastFloodEventFeature) => inside(f),
      filterRange: [0, 1],
      collisionTestProps: { sizeScale: 4 },
        updateTriggers: {
          // getText: [currentTime],
          // getPosition: [currentTime],
          // getCollisionPriority: [currentTime],
          getFilterValue: [t0],
        },
    })
  ])

  // new TextLayer({
  //     id: 'events-label-focus',
  //     data: features,
  //     getPosition: (f: any) => [...f.geometry.coordinates, f.properties.depth_inches * 25.4 / 1000 + 1],
  //     getText: f => {
  //       const depth = f.properties.depth_inches;
  //       // return depth ? `${(Math.round(depth*10)/10).toFixed(1)}` : '';
  //       return depth ? `${(depth > 6 ? depth.toFixed(0) : depth.toFixed(1))}` : '';
  //     },
  //     getSize: f => {
  //       const depth = f.properties.depth_inches;
  //       return depth ? (depth > 6 ? 16 : 12) : 0;
  //     },

  //     getAlignmentBaseline: 'center',
  //     billboard: true,
  //     getAnchor: 'middle',
  //     getColor: [255, 255, 255, 255],
  //     // getSize: 12,
  //     fontWeight: 900,
  //     fontFamily: "'Verdana, Roboto, Helvetica Neue', Arial, Helvetica, sans-serif",
  //     parameters: { depthTest: false },
  //     collisionGroup: 'depth-labels',

  //     getCollisionPriority: function(f) {
  //       return Math.round(f.properties.depth_inches) * 25.4 - 1000;
  //     },
  //     getFilterValue: function(f) {
  //       const t1 = f.properties.time.getTime();
  //       // return t0 >= t1 && Math.abs(t0 - t1 - closestTimeDelta[f.id!.toString()]) < 1 ? 1 : -1;
  //       const depth = f.properties.depth_inches;
  //       return t0 >= t1 && t1 > t0 - 30 * 60 * 1000 && Math.abs(t0 - t1 - closestTimeDelta[f.id!.toString()]) < 1 ? 1 : -1;
  //     },
  //     filterRange: [0, 1],
  //     extensions: [
  //       new CollisionFilterExtension(),
  //       new DataFilterExtension({ filterSize: 1 })
  //     ],
  //     collisionTestProps: { sizeScale: 3 },
  //       updateTriggers: {
  //         // getText: [currentTime],
  //         // getPosition: [currentTime],
  //         // getCollisionPriority: [currentTime],
  //         getFilterValue: [t0],
  //       },
  //   })

  // return (
  //   !features ? [] : [
  //     new ScatterplotLayer({
  //     id: 'past_flood_events',
  //     data: features,
  //     radiusUnits: 'pixels' as 'pixels',
  //     stroked: true,
  //     filled: true,
  //     pickable: true,
  //     autoHighlight: true,
  //     // getLineColor: [0, 120, 200, 200],
  //     getLineColor: [255, 255, 255, 200],
  //     lineWidthMinPixels: 2,

  //     minZoom: 8,
  //     maxZoom: 14,
  //     getFillColor: (f) => depthToColor(f.properties.max_depth_inches),
  //     getRadius: (f) => { return Math.round(4 * (1 + f.properties.max_depth_inches / 8)); },
  //     onClick,

  //     // getFilterValue: (d) => d.properties.fk_status !== 'hidden' ? 1 : -1,
  //     // filterRange: [0, 1],
  //     // extensions: [
  //     //   new DataFilterExtension({ filterSize: 1 })
  //     // ],
  //   })
  //   ]
  // )
}
